import { request } from '../axios/axios';

const REPOSITORY_DATE_FIELDS = [ 'last_processed_date' ];
const PROJECT_DATE_FIELDS = [ 'last_processed_date', 'last_commit_date' ];

class DataAccess {
  private transformDateFields(entity: Record<string, Date>, dateFields: string[]) {
    for (const dateField of dateFields) {
      if (entity[dateField]) {
        entity[dateField] = new Date(entity[dateField]);
      }
    }
  }

  private transformProject(project: any): any {
    this.transformDateFields(project, PROJECT_DATE_FIELDS);
  }

  private transformRepository(repository: any) {
    this.transformDateFields(repository, REPOSITORY_DATE_FIELDS);
    if (repository.projects) {
      for (const project of repository.projects) {
        this.transformProject(project);
      }
    }
  }

  public async getUser(): Promise<any> {
    const { response, error } = await request.get<any>('/user', { skipNotificationsForErrors: [401] });
    return [ response || null, error ];
  }

  public async getRepositories(): Promise<any> {
    const { response, error } = await request.get<any>('/repositories');
    return [ response || null, error ];
  }

  public async getRepositoryProjectList(): Promise<any> {
    const { response, error } = await request.get<any>('/repositories/projects');
    if (error) {
      return [ null, error ];
    }

    for (const repository of response) {
      this.transformRepository(repository);
    }

    return [ response, null ];
  }

  public async getLogByProjectId(projectId: number): Promise<any> {
    const { response, error } = await request.get<any>(`/projects/${projectId}/log`);
    return [ response || null, error ];
  }

  public async getContributorsList(): Promise<any> {
    const { response, error } = await request.get<any>('/contributors');
    return [ response || null, error ];
  }

  public async editContributor(id: string, payload: any): Promise<any> {
    const { response, error } = await request.put(`/contributors/${id}`, payload);
    return [ response || null, error ];
  }

  public async mergeContributors(sourceId: string, targetId: string): Promise<any> {
    const { response, error } = await request.post(`/contributors/${sourceId}/merge/${targetId}`);
    return [ response || null, error ];
  }

  public async detachCommiter(id: number): Promise<any> {
    const { response, error } = await request.post(`/committers/${id}/detach`);
    return [ response || null, error ];
  }

  public async attachCommiterToContributor(commiterId: string, contributorId: string): Promise<any> {
    const { response, error } = await request.post(`/committers/${commiterId}/attach/${contributorId}`);
    return [ response || null, error ];
  }

  public async getAllCommits(id: string): Promise<any> {
    const { response, error } = await request.get(`/commits?user_id=${id}`);
    return [ response || null, error ];
  }

  public async getSpecificCommits(id: string, date_from: string, date_to: string): Promise<any> {
    const { response, error } = await request.get(`/commits?user_id=${id}&date_from=${date_from}&date_to=${date_to}`);
    return [ response || null, error ];
  }

  public async getCrawlerLastLog(): Promise<any> {
    const { response, error } = await request.get('/crawler/log');
    return [ response || null, error ];
  }

  public async getTogglUserList(): Promise<any> {
    const { response, error } = await request.get('/toggl/users');
    return [ response || null, error ];
  }

  public async getTogglTotals(contributorId: string, date_from: string, date_to: string): Promise<any> {
    if (date_from === '' || date_to === '') {
      const { response, error } = await request.get(`/toggl/${contributorId}/total`);
      return [ response || null, error ];
    } else {
      const { response, error } = await request.get(
        `/toggl/${contributorId}/total?date_from=${date_from}&date_to=${date_to}`
      );
      return [ response || null, error ];
    }
  }
}

export const dataAccess = new DataAccess();
